import React, {
  useCallback,
  useMemo,
  useRef,
  useState,
} from "react";
import "./turbine-lightning-ruler.css";
import turbineIcon from "../../assets/turbine_img.png";
import blackLightning from "../../assets/black_lightning.svg";

// Where 0 m and the ruler's last distance sit, as the notches under the track are drawn: the
// distance track is 8px narrower than the lightning track and each notch is 3px in. Strikes
// are placed on the same scale so a strike at 3000 m lands on the 3000 m notch.
const AXIS_START_PX = 3;
const AXIS_SHORTER_BY_PX = 8;
// The strike marks are positioned against the whole ruler, whose 8px padding either side puts
// its edge 8px outside the track.
const RULER_PADDING_PX = 8;
const MARK_START = `${RULER_PADDING_PX + AXIS_START_PX}px`;
const MARK_SPAN = `(100% - ${2 * RULER_PADDING_PX + AXIS_SHORTER_BY_PX}px)`;


export default function TurbineLightningRuler({
                                                  strikes,
                                                  from = null,
                                                  to = null,
                                                  onStrikeHover,
                                                  onMouseLeave

                                              }) {
    const [closestTo, setClosestTo] = useState(null);
    const trackRef = useRef(null);

    const maxDistance = useMemo(() => {
        if (strikes.length === 0) return Math.max(2, from ?? 2, to ?? 2);

        // Walked rather than spread into Math.max: a multi-year selection is hundreds of
        // thousands of strikes, and spreading that many arguments overflows the call stack.
        let furthest = 0;
        for (const strike of strikes) {
            if (strike.distance_km > furthest) furthest = strike.distance_km;
        }
        const closest = Math.ceil(furthest);

        return Math.max(closest, 2, from ?? closest, to ?? 2);
    }, [strikes, from, to]);

    const mouseMove = useCallback(
        (event) => {
            if (!trackRef.current || !strikes.length) return;
            const mouseLeft = event.clientX;
            const rect = trackRef.current.getBoundingClientRect();

            const percentualDistFromLeft =
                (mouseLeft - rect.left - AXIS_START_PX) / (rect.width - AXIS_SHORTER_BY_PX);

            // Single pass instead of copying and sorting the whole array: this runs on every
            // mouse move, and the array can hold hundreds of thousands of strikes.
            let closestStrike = null;
            let smallestGap = Infinity;
            for (const strike of strikes) {
                const gap = Math.abs(
                    strike.distance_km / maxDistance - percentualDistFromLeft
                );
                if (gap < smallestGap) {
                    smallestGap = gap;
                    closestStrike = strike;
                }
            }

            setClosestTo(closestStrike);

            if (onStrikeHover) {
                onStrikeHover(closestStrike);
            }
        },
        [trackRef, strikes, maxDistance, onStrikeHover]
    );

    const tickerCount = Math.min(Math.ceil(maxDistance / 0.5), 5);

    // Both the position and the width of a mark are rounded to whole percents, so strikes at
    // a similar distance with a similar uncertainty draw the exact same element on top of
    // each other. Only one of each is worth putting in the DOM - a multi-year selection is
    // tens of thousands of strikes and three nodes apiece was most of the page.
    // Hover still works off the full list, so nothing is lost from the interaction.
    const marks = useMemo(() => {
        const byPosition = new Map();

        strikes.forEach((strike) => {
            // The orange underline reflects the detection's positional uncertainty: the
            // bigger the confidence ellipse, the longer the bar. The bar is centered on the
            // strike, so its width is the full uncertainty diameter (2 x the semi-major
            // axis, the worst-case radius).
            const uncertaintyKm = 2 * (strike.semi_major_axis ?? 0);
            const left = Math.round((strike.distance_km / maxDistance) * 100);
            const width = Math.round((uncertaintyKm / maxDistance) * 100);

            const key = `${left}|${width}`;
            let mark = byPosition.get(key);
            if (!mark) {
                // Near either end the uncertainty reaches past the ruler - below 0 m, or past
                // the last distance on it - so the bar is cut to the ruler. As a share of the
                // mark's own width, since the bar is laid out inside it.
                const barStart = Math.max(0, left - width / 2);
                const barEnd = Math.min(100, left + width / 2);
                const bar = width > 0
                    ? {
                        offset: ((barStart - (left - width / 2)) / width) * 100,
                        width: (Math.max(0, barEnd - barStart) / width) * 100,
                    }
                    : { offset: 0, width: 0 };

                mark = { key, left, width, bar, ids: new Set() };
                byPosition.set(key, mark);
            }
            mark.ids.add(strike.id);
        });

        return [...byPosition.values()];
    }, [strikes, maxDistance]);

    return (
        <div className={"turbine-lightning-ruler"}>
            <div
                className="lightning-track"
                ref={trackRef}
                onMouseMove={mouseMove}
                onMouseLeave={() => {
                    setClosestTo(null);
                    if (onMouseLeave) onMouseLeave();
                }}
            >
                <img src={turbineIcon} alt="turbine" className="ruler-turbine-icon"/>
                {marks.map((mark) => (
                    <div
                        className="lightning-image-wrapper"
                        key={mark.key}
                        style={{
                            left: `calc(${MARK_START} + ${MARK_SPAN} * ${mark.left / 100})`,
                            width: `calc(${MARK_SPAN} * ${mark.width / 100})`,
                        }}
                    >
                        <img
                            className={`lightning-image ${
                                closestTo && mark.ids.has(closestTo.id) ? "hovering" : ""
                            }`}
                            src={blackLightning}
                            alt="lightning-strike"
                        />
                        <div
                            className="uncertainty-bar"
                            style={{
                                marginLeft: `${mark.bar.offset}%`,
                                width: `${mark.bar.width}%`,
                            }}
                        />
                    </div>
                ))}
                {from !== null && to !== null && (
                    <div
                        className="progress"
                        style={{
                            left: `calc(${(to / maxDistance) * 100}% - 8px)`,
                            width: `${((from - to) / maxDistance) * 100}%`,
                        }}
                    />
                )}
            </div>
            <div className="distance-track">
                {Array(tickerCount + 1)
                    .fill(0)
                    .map((_, idx) => (
                        <div
                            key={idx}
                            className="notch"
                            style={{
                                position: "absolute",
                                left: `calc(${Math.round((idx / tickerCount) * 100)}% + 3px)`,
                            }}
                        />
                    ))}
                {Array(tickerCount + 1)
                    .fill(0)
                    .map((_, idx) => (
                        <label
                            key={idx}
                            style={{
                                position: "absolute",
                                left: `calc(${Math.round((idx / tickerCount) * 100)}%)`,
                            }}
                        >
                            {Math.round(idx * (maxDistance / tickerCount) * 10) * 100}m
                        </label>
                    ))}
            </div>
        </div>
    );
}
